import React from 'react';

import { SortableTable } from '~/components/core';
import ColoredDot from '~/components/core/Atomic/ColoredDot';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { ArrowDownwardIcon, ArrowUpwardIcon } from '~/components/icons';
import InlineIconButton from '~/components/InlineIconButton';
import type { NotificationKanbanCategory } from '~/components/Notifications/Kanban/types';
import useOrganization from '~/components/OrganizationContext';
import type { CategoryModel } from '~/components/SystemConfiguration/NotificationsConfiguration/NotificationsCategories';
import Switch from '~/components/ui/atoms/Switch';

interface KanbanConfigurationTableProps {
  categories?: NotificationKanbanCategory[];
  isError: boolean;
  isLoading: boolean;
  onMoveUp: (category: NotificationKanbanCategory) => void | Promise<void>;
  onMoveDown: (category: NotificationKanbanCategory) => void | Promise<void>;
  onShowToggle: (category: NotificationKanbanCategory) => void | Promise<void>;
}

const KanbanConfigurationTable: React.FC<KanbanConfigurationTableProps> = ({
  categories,
  isError = false,
  isLoading = false,
  onMoveUp,
  onMoveDown,
  onShowToggle,
}) => {
  const { notificationCategories } = useOrganization() as { notificationCategories: CategoryModel[] };
  const getNotificationCategory = (categoryId: number) => {
    return notificationCategories.find((category) => category.id === categoryId);
  };

  const columns = [
    {
      id: 'order',
      disableSort: true,
      label: 'Display Order',
      width: '120px',
      specialCell: (category: NotificationKanbanCategory) => (
        <div className="flex items-center">
          <span className="mr-12">
            <InlineIconButton
              icon={ArrowUpwardIcon}
              className="hover:cursor-pointer"
              onClick={() => onMoveUp(category)}
              disabled={category.kanban_column_index === categories?.[0].kanban_column_index}
              defaultColor={undefined}
              iconStyle={undefined}
              tooltipTitle={undefined}
              useIconButton={undefined}
              ignorePermissions={undefined}
              ariaLabel={undefined}
            />
          </span>
          <InlineIconButton
            icon={ArrowDownwardIcon}
            className="hover:cursor-pointer"
            onClick={() => onMoveDown(category)}
            disabled={category.kanban_column_index === categories?.[categories.length - 1].kanban_column_index}
            defaultColor={undefined}
            iconStyle={undefined}
            tooltipTitle={undefined}
            useIconButton={undefined}
            ignorePermissions={undefined}
            ariaLabel={undefined}
          />
        </div>
      ),
    },
    {
      id: 'color',
      label: 'Color',
      disableSort: true,
      specialCell: (category: NotificationKanbanCategory) => {
        const color = getNotificationCategory(category.category_id)?.color;
        return <ColoredDot color={color || 'grey-600'} />;
      },
    },
    {
      id: 'category_name',
      label: 'Category Name',
      specialCell: (category: NotificationKanbanCategory) => getNotificationCategory(category.category_id)?.label,
    },
    {
      id: 'is_enabled',
      label: 'Show',
      specialCell: (category: NotificationKanbanCategory) => (
        <Switch checked={category.is_enabled} disabled={false} onChange={() => onShowToggle(category)} />
      ),
    },
  ];

  return (
    <>
      {categories && (
        <SortableTable
          columns={columns}
          rows={categories.map((category) => ({ ...category, id: category.category_id }))}
        />
      )}
      {(isError || isLoading) && <SkeletonTable rowsCount={4} columnsCount={10} isError={isError} />}
    </>
  );
};

export default KanbanConfigurationTable;
